import React, { useEffect, useState } from "react";
import { AuthNav } from "../../../components/Navbar/Webpages/AuthNav";
import { SideNav } from "../../../components/Navbar/Webpages/AuthNav";

import { PlanCard } from "../../../components/Cards/Plans";
import { AccountBalanceCard } from "../../../components/Cards/AccountDetails";
import balanceImgOne from "../../../asset/balanceimgone.jpeg";
import balanceImgTwo from "../../../asset/balanceimgtwo.jpeg";

import { useDispatch, useSelector } from "react-redux";
import {
  getLogUserRequest,
  getPlansRequest,
  getUserInvestmentsRequest,
  getUserPaymentsRequest,
  claimRewardRequest,
} from "../../../redux/action";
import toast from "react-hot-toast";
import { CircularProgress } from "@material-ui/core";
import { MarketOverview } from "react-tradingview-embed";

import { ReferralCard } from "../../../components/Cards/ReferralCard";
import { InvestmentCard } from "../../../components/Cards/UserInvestmentCard";
import { QuickActionsBar } from "../../../components/Cards/QuickActions";
import Withdrawals from "../Withdrawals";
import MonthlyProfitsChart from "../../../components/Cards/ChartCards";

const Home = () => {
  const [activeInvestment, setActiveInvestment] = useState(0);
  const [expectedReturn, setExpectedReturn] = useState(0);
  const [pendingWithdraw, setPendingWithdraw] = useState(0);
  const [marketWidgetReady, setMarketWidgetReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMarketWidgetReady(true), 500);
    return () => clearTimeout(t);
  }, []);

  const dispatch = useDispatch();

  const {
    getPlansError,
    getPlansLoading,
    user,
    plans,
    getUserInvestmentsError,
    getLogUserError,
    investments,
    getUserInvestmentsLoading,
    token,
    getUserPaymentsLoading,
    payments,
  } = useSelector((state) => {
    const {
      errors: {
        getPlans: getPlansError,
        getUserInvestments: getUserInvestmentsError,
        getLogUser: getLogUserError,
        getUserPayments: getUserPaymentsError,
      },
    } = state.ajaxStatuses;

    const {
      getPlansLoading,
      getUserInvestmentsLoading,
      getLogUserLoading,
      getUserPaymentsLoading,
    } = state.loadingIndicator;

    const { user, token } = state.userData;
    const { plans } = state.planData;
    const { investments } = state.investmentData;
    const { payments } = state.paymentData;

    return {
      getPlansError,
      getPlansLoading,
      user,
      plans,
      getUserInvestmentsError,
      getLogUserError,
      investments,
      getUserInvestmentsLoading,
      token,
      getLogUserLoading,
      getUserPaymentsError,
      getUserPaymentsLoading,
      payments,
    };
  });

  // --- Error Handling Effects ---
  useEffect(() => {
    if (getPlansError) toast.error(getPlansError);
    if (getLogUserError) toast.error(getLogUserError);
    if (getUserInvestmentsError) toast.error(getUserInvestmentsError);
  }, [getPlansError, getLogUserError, getUserInvestmentsError]);

  useEffect(() => {
    if (user?._id && token) {
      // Attempt to claim reward
      dispatch(claimRewardRequest({ token }));
    }
  }, [user?._id, token, dispatch]);

  // --- Data Fetching ---
  useEffect(() => {
    dispatch(getPlansRequest());
    dispatch(getUserInvestmentsRequest(user._id));
    dispatch(getLogUserRequest(token));
    dispatch(getUserPaymentsRequest(user._id));
  }, [dispatch, user._id, token]);

  // --- Scroll Fix ---
  useEffect(() => {
    document.body.style.overflow = "auto";
    document.body.style.overflowX = "hidden";
    document.body.style.height = "auto";
    document.body.classList.remove("overflow-hidden");
    window.scrollTo(0, 0);

    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, []);

  // --- Calculations ---
  useEffect(() => {
    if (!getUserInvestmentsLoading) {
      let activeInvest = 0;
      let expectedInvest = 0;
      investments.forEach((investment) => {
        if (investment.isActive) {
          activeInvest += investment.amount;
          expectedInvest += investment.expectedReturn;
        }
      });

      setActiveInvestment(activeInvest);
      setExpectedReturn(expectedInvest);
    }
  }, [getUserInvestmentsLoading, investments]);

  useEffect(() => {
    if (!getUserPaymentsLoading) {
      let withAmount = 0;
      if (payments && Array.isArray(payments)) {
        payments.forEach((data) => {
          if (data.type === "Withdrawal" && data.status === "Pending")
            withAmount += data.amount;
        });
      }
      setPendingWithdraw(withAmount);
    }
  }, [getUserPaymentsLoading, payments]);

  // Helper to determine greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  return (
    <div className="min-h-screen bg-gray-50/50">
      <AuthNav title={"Dashboard"} />
      <SideNav />

      {/* --- SINGLE MAIN CONTAINER --- */}
      <div className="container max-w-screen-xl mx-auto flex flex-col md:grid md:grid-cols-4 px-4 py-6 pb-24 gap-6 pt-20">
        <div className="w-full space-y-4 col-span-3">
          {/* 1. Header & User Info */}
          <div className="flex items-center justify-between w-full py-4 bg-transparent">
            {/* Left: Greeting & Updates */}
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">
                {getGreeting()}
              </h1>
              <p className="text-[13px] text-gray-500 font-medium">
                Payments Updates
              </p>
            </div>

            {/* Center: Search Bar (Hidden on mobile, visible on md+) */}
            <div className="hidden md:flex flex-1 max-w-md mx-8">
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-transparent bg-white rounded-xl leading-5 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-gray-200 sm:text-sm shadow-sm"
                  placeholder="Search"
                />
              </div>
            </div>

            {/* Right: User Avatar */}
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-[#403d39] flex items-center justify-center text-white border-2 border-white shadow-sm overflow-hidden cursor-pointer">
                <span className="text-sm font-bold tracking-tighter">
                  {user.firstName?.charAt(0)?.toUpperCase()}
                  {user.lastName?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* 4. Account Overview Grid */}
          <div>
            <h1 className="text-xl font-semibold font-heading text-gray-800 mb-4">
              Account Overview
            </h1>
            {getUserInvestmentsLoading ? (
              <div className="flex justify-center py-10">
                <CircularProgress size="20px" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <AccountBalanceCard
                  title={"Total Balance"}
                  data={user.balance}
                  img={balanceImgOne}
                />
                <AccountBalanceCard
                  title={"Active"}
                  data={activeInvestment}
                  img={balanceImgTwo}
                />
                <AccountBalanceCard
                  title={"Return"}
                  data={expectedReturn}
                  img={balanceImgOne}
                />
                {/* <AccountBalanceCard
                  title={"Pending Withdraw"}
                  data={pendingWithdraw}
                /> */}
                {/* <AccountBalanceCard
                  title={"Referral Bonus"}
                  data={user.referralBonus || 0}
                /> */}

                <ReferralCard
                  referralCount={user.referralCount}
                  referralLink={user.referralLink}
                />
              </div>
            )}
          </div>

          {/* 3. Quick Actions */}
          <div>
            <QuickActionsBar />
          </div>

          {/* 7. Market Overview Widget */}
          <div className="bg-white rounded-xl shadow-lg p-4 overflow-hidden border border-gray-100">
            {marketWidgetReady && (
              <MarketOverview
                widgetProps={{
                  colorTheme: "light",
                  dateRange: "12M",
                  showChart: true,
                  locale: "en",
                  largeChartUrl: "",
                  isTransparent: true,
                  showSymbolLogo: true,
                  width: "100%",
                  height: "400",
                  tabs: [
                    {
                      title: "Crypto",
                      symbols: [
                        { s: "BINANCE:BTCUSDT", d: "Bitcoin" },
                        { s: "BINANCE:ETHUSDT", d: "Ethereum" },
                        { s: "BINANCE:BNBUSDT", d: "BNB" },
                        { s: "BINANCE:SOLUSDT", d: "Solana" },
                        { s: "BINANCE:MATICUSDT", d: "Polygon" },
                      ],
                    },
                  ],
                }}
              />
            )}
          </div>

          {/* 6. Investments Section */}
          <div>
            <h1 className="text-xl font-semibold font-heading text-gray-800 mb-4">
              Investments
            </h1>

            {getUserInvestmentsLoading ? (
              <div className="flex justify-center py-20">
                <CircularProgress size="20px" />
              </div>
            ) : investments.filter((investment) => investment.isActive).length <
              1 ? (
              // Empty State: Show Plans
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <h1 className="text-lg font-body text-gray-600 mb-6 text-center">
                  You have no active investments. Choose a plan below to start
                  growing your portfolio.
                </h1>
                <div className="overflow-x-auto pb-4">
                  <div className="flex md:grid md:grid-cols-3 gap-6 min-w-max md:min-w-0">
                    {getPlansLoading ? (
                      <div className="col-span-3 text-center py-10">
                        <CircularProgress size="20px" />
                      </div>
                    ) : (
                      plans.map((plan, index) => (
                        <div key={index} className="w-80 md:w-auto">
                          <PlanCard plan={plan} />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Active Investments List
              <div className="flex flex-col space-y-4">
                {investments
                  .filter((investment) => investment.isActive)
                  .map((investment, index) => (
                    <InvestmentCard key={index} investment={investment} />
                  ))}
              </div>
            )}
          </div>
        </div>
        <div className=" col-span-1 flex flex-col gap-y-3">
          <MonthlyProfitsChart
            balance={user.balance}
            activeInvestment={activeInvestment}
            expectedReturn={expectedReturn}
          />
          <Withdrawals isRenderedHome={false} />
        </div>
      </div>
    </div>
  );
};

export default Home;
